// insurance-information.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ReactiveFormsModule } from '@angular/forms';
import { FormConfigService } from '../forms-config.service';
import { AppStateService } from '../../app-state.service';
import { MaterialModule } from '../../material.module';
import { PatientProfile } from '../patient.model';
import { jsPDF } from 'jspdf';


@Component({
  selector: 'app-insurance-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCardModule,
    MatCheckboxModule
  ],
  templateUrl: './insurance-management.component.html',
  styleUrls: ['./insurance-management.component.scss']
})
export class InsuranceManagementComponent implements OnInit {
  formConfigService = inject(FormConfigService);
  appStateService = inject(AppStateService);
  snackBar = inject(MatSnackBar);
  fb = inject(FormBuilder);
  router = inject(Router);

  clientId: string = '';
  patientId: string = '';
  loading = false;
  isFormEdited = false;
  selectedFiles: { [key: string]: File } = {};
  filePreviewUrls: { [key: string]: string } = {};

  // Insurance form for patient data
  insuranceForm: FormGroup;

  // Insurance provider options - loaded from backend
  insuranceProviders: string[] = [];
  showSecondaryInsurance = false;

  constructor() {
    this.clientId = this.appStateService.getState().clientId || '';
    this.patientId = this.appStateService.getState().patientId || '';

    this.insuranceForm = this.fb.group({
      // Primary Insurance Fields
      primaryInsuranceProvider: [''],
      primarySubscriberId: [''],
      primaryGroupNumber: [''],
      primaryPolicyHolderName: [''],
      primaryPolicyHolderDOB: [''],
      // Secondary Insurance Fields (hidden by default)
      hasSecondaryInsurance: [false],
      secondaryInsuranceProvider: [''],
      secondarySubscriberId: [''],
      secondaryGroupNumber: [''],
      secondaryPolicyHolderName: [''],
      secondaryPolicyHolderDOB: ['']
    });

    this.insuranceForm.valueChanges.subscribe(() => {
      this.isFormEdited = true;
    });
  }

  ngOnInit(): void {
    this.loadInsuranceProviders();
    this.loadPatientInsuranceData();
  }


  loadInsuranceProviders(): void {
    this.formConfigService.fetchInsuranceProviders(this.clientId).subscribe(
      (response: any) => {
        if (response && response.providers) {
          this.insuranceProviders = response.providers;
        }
      },
      error => {
        // Fall back to default providers if API fails
        this.insuranceProviders = [
          'Delta Dental', 'Cigna Dental', 'Aetna', 'MetLife', 'Humana', 'Guardian',
          'Principal', 'UnitedHealthcare', 'Blue Cross Blue Shield', 'Anthem',
          'Medicaid', 'Medicare', 'No Insurance/Self Pay', 'Other'
        ];
      }
    );
  }

  loadPatientInsuranceData(): void {
    this.formConfigService.fetchPatient(this.clientId, this.patientId).subscribe(
      (patientData: PatientProfile) => {
        // Extract insurance data from patient profile (excluding card images - handled by document upload)
        const insuranceData = {
          primaryInsuranceProvider: patientData.primaryInsuranceProvider || '',
          primarySubscriberId: patientData.primarySubscriberId || '',
          primaryGroupNumber: patientData.primaryGroupNumber || '',
          primaryPolicyHolderName: (patientData as any).primaryPolicyHolderName || '',
          primaryPolicyHolderDOB: (patientData as any).primaryPolicyHolderDOB || '',
          hasSecondaryInsurance: !!patientData.secondaryInsuranceProvider,
          secondaryInsuranceProvider: patientData.secondaryInsuranceProvider || '',
          secondarySubscriberId: patientData.secondarySubscriberId || '',
          secondaryGroupNumber: patientData.secondaryGroupNumber || '',
          secondaryPolicyHolderName: (patientData as any).secondaryPolicyHolderName || '',
          secondaryPolicyHolderDOB: (patientData as any).secondaryPolicyHolderDOB || ''
        };

        this.insuranceForm.patchValue(insuranceData);
        this.isFormEdited = false;
      },
      error => {
        this.showError('Error loading insurance information.');
      }
    );
  }

  async onSave(): Promise<void> {
    if (this.insuranceForm.valid && this.isFormEdited) {
      const formValues = this.insuranceForm.getRawValue();
      this.loading = true;

      try {
        // Prepare submission data following the form submission pattern
        const submissionData: any = {
          form_data: {
            insurance_information: {
              // Primary insurance data
              primaryInsuranceProvider: formValues.primaryInsuranceProvider || '',
              primarySubscriberId: formValues.primarySubscriberId || '',
              primaryGroupNumber: formValues.primaryGroupNumber || '',
              primaryPolicyHolderName: formValues.primaryPolicyHolderName || '',
              primaryPolicyHolderDOB: formValues.primaryPolicyHolderDOB || '',
              // Secondary insurance data
              hasSecondaryInsurance: formValues.hasSecondaryInsurance,
              secondaryInsuranceProvider: formValues.secondaryInsuranceProvider || '',
              secondarySubscriberId: formValues.secondarySubscriberId || '',
              secondaryGroupNumber: formValues.secondaryGroupNumber || '',
              secondaryPolicyHolderName: formValues.secondaryPolicyHolderName || '',
              secondaryPolicyHolderDOB: formValues.secondaryPolicyHolderDOB || ''
            }
          },
          documents_data: {}
        };

        // Add insurance card images as base64 if selected
        if (this.filePreviewUrls['primaryInsuranceCardFront']) {
          submissionData.documents_data.primaryInsuranceCardFront = this.filePreviewUrls['primaryInsuranceCardFront'];
        }
        if (this.filePreviewUrls['primaryInsuranceCardBack']) {
          submissionData.documents_data.primaryInsuranceCardBack = this.filePreviewUrls['primaryInsuranceCardBack'];
        }
        if (this.filePreviewUrls['secondaryInsuranceCardFront']) {
          submissionData.documents_data.secondaryInsuranceCardFront = this.filePreviewUrls['secondaryInsuranceCardFront'];
        }
        if (this.filePreviewUrls['secondaryInsuranceCardBack']) {
          submissionData.documents_data.secondaryInsuranceCardBack = this.filePreviewUrls['secondaryInsuranceCardBack'];
        }

        // Submit insurance form data - backend will generate PDF and upload
        this.formConfigService.submitInsuranceForm(this.clientId, this.patientId, submissionData).subscribe(
          () => {
            this.loading = false;
            this.snackBar.open('Insurance information saved successfully!', 'Close', {
              duration: 3000,
            });
            this.isFormEdited = false;
            // Clear selected files after successful save
            this.selectedFiles = {};
            this.filePreviewUrls = {};
            // Navigate back to main dashboard
            this.router.navigate(['/dashboard/main']);
          },
          error => {
            this.loading = false;
            this.snackBar.open('Failed to save insurance information', 'Close', {
              duration: 5000,
            });
          }
        );
      } catch (error) {
        this.loading = false;
        this.snackBar.open('Error processing insurance information', 'Close', {
          duration: 5000,
        });
      }
    } else if (!this.isFormEdited) {
      this.snackBar.open('No changes detected in the form', 'Close', {
        duration: 3000,
      });
    } else {
      this.snackBar.open('Please correct the errors in the form', 'Close', {
        duration: 5000,
      });
    }
  }

  private async generateInsurancePDF(formData: any): Promise<Blob> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPos = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Header
    pdf.setFontSize(18);
    pdf.setTextColor(66, 0, 238);
    pdf.text('Insurance Information', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Date
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPos);
    yPos += 10;

    // Primary Insurance Section
    if (formData.primaryInsuranceProvider) {
      pdf.setFontSize(14);
      pdf.setTextColor(66, 0, 238);
      pdf.text('Primary Insurance', 14, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      const primaryInfo = [
        ['Provider:', formData.primaryInsuranceProvider || 'Not provided'],
        ['Member ID:', formData.primarySubscriberId || 'Not provided'],
        ['Group Number:', formData.primaryGroupNumber || 'Not provided'],
        ['Policy Holder:', formData.primaryPolicyHolderName || 'Self'],
        ['Policy Holder DOB:', formData.primaryPolicyHolderDOB || 'Not provided']
      ];

      primaryInfo.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, 20, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 60, yPos);
        yPos += 7;
      });

      yPos += 5;
    }

    // Secondary Insurance Section
    if (formData.hasSecondaryInsurance && formData.secondaryInsuranceProvider) {
      pdf.setFontSize(14);
      pdf.setTextColor(66, 0, 238);
      pdf.text('Secondary Insurance', 14, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      const secondaryInfo = [
        ['Provider:', formData.secondaryInsuranceProvider || 'Not provided'],
        ['Member ID:', formData.secondarySubscriberId || 'Not provided'],
        ['Group Number:', formData.secondaryGroupNumber || 'Not provided'],
        ['Policy Holder:', formData.secondaryPolicyHolderName || 'Self'],
        ['Policy Holder DOB:', formData.secondaryPolicyHolderDOB || 'Not provided']
      ];

      secondaryInfo.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, 20, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 60, yPos);
        yPos += 7;
      });
    }

    // Footer
    yPos = pdf.internal.pageSize.getHeight() - 20;
    pdf.setFontSize(8);
    pdf.setTextColor(150, 150, 150);
    pdf.text('This document contains patient insurance information', pageWidth / 2, yPos, { align: 'center' });

    // Convert to blob
    return pdf.output('blob');
  }

  async onFileSelected(event: Event, fieldName: string): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      // Check file size (limit to 10MB)
      if (file.size > 10 * 1024 * 1024) {
        this.showError('File size must be less than 10MB');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        this.showError('Please select an image file');
        return;
      }

      // Store file locally (don't upload yet)
      this.selectedFiles[fieldName] = file;

      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        this.filePreviewUrls[fieldName] = reader.result as string;
      };
      reader.readAsDataURL(file);

      this.isFormEdited = true;
      this.snackBar.open(`${this.getDescription(fieldName)} selected`, 'Close', {
        duration: 2000,
      });
    }
  }

  private async combineInsuranceDataAndImages(formData: any): Promise<Blob> {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPos = 20;
    const pageWidth = pdf.internal.pageSize.getWidth();

    // Header
    pdf.setFontSize(18);
    pdf.setTextColor(66, 0, 238);
    pdf.text('Insurance Information', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Date
    pdf.setFontSize(10);
    pdf.setTextColor(100, 100, 100);
    pdf.text(`Generated: ${new Date().toLocaleDateString()}`, 14, yPos);
    yPos += 10;

    // Primary Insurance Section
    if (formData.primaryInsuranceProvider) {
      pdf.setFontSize(14);
      pdf.setTextColor(66, 0, 238);
      pdf.text('Primary Insurance', 14, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      const primaryInfo = [
        ['Provider:', formData.primaryInsuranceProvider || 'Not provided'],
        ['Member ID:', formData.primarySubscriberId || 'Not provided'],
        ['Group Number:', formData.primaryGroupNumber || 'Not provided'],
        ['Policy Holder:', formData.primaryPolicyHolderName || 'Self'],
        ['Policy Holder DOB:', formData.primaryPolicyHolderDOB || 'Not provided']
      ];

      primaryInfo.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, 20, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 60, yPos);
        yPos += 7;
      });

      // Add primary insurance card images if available
      if (this.filePreviewUrls['primaryInsuranceCardFront']) {
        pdf.addPage();
        pdf.setFontSize(12);
        pdf.text('Primary Insurance Card - Front', pageWidth / 2, 20, { align: 'center' });
        pdf.addImage(this.filePreviewUrls['primaryInsuranceCardFront'], 'JPEG', 20, 30, 170, 100);
      }

      if (this.filePreviewUrls['primaryInsuranceCardBack']) {
        pdf.addPage();
        pdf.setFontSize(12);
        pdf.text('Primary Insurance Card - Back', pageWidth / 2, 20, { align: 'center' });
        pdf.addImage(this.filePreviewUrls['primaryInsuranceCardBack'], 'JPEG', 20, 30, 170, 100);
      }
    }

    // Secondary Insurance Section (if applicable)
    if (formData.hasSecondaryInsurance && formData.secondaryInsuranceProvider) {
      // Start new page for secondary insurance
      pdf.addPage();
      yPos = 20;

      pdf.setFontSize(14);
      pdf.setTextColor(66, 0, 238);
      pdf.text('Secondary Insurance', 14, yPos);
      yPos += 8;

      pdf.setFontSize(11);
      pdf.setTextColor(0, 0, 0);

      const secondaryInfo = [
        ['Provider:', formData.secondaryInsuranceProvider || 'Not provided'],
        ['Member ID:', formData.secondarySubscriberId || 'Not provided'],
        ['Group Number:', formData.secondaryGroupNumber || 'Not provided'],
        ['Policy Holder:', formData.secondaryPolicyHolderName || 'Self'],
        ['Policy Holder DOB:', formData.secondaryPolicyHolderDOB || 'Not provided']
      ];

      secondaryInfo.forEach(([label, value]) => {
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, 20, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(value, 60, yPos);
        yPos += 7;
      });

      // Add secondary insurance card images if available
      if (this.filePreviewUrls['secondaryInsuranceCardFront']) {
        pdf.addPage();
        pdf.setFontSize(12);
        pdf.text('Secondary Insurance Card - Front', pageWidth / 2, 20, { align: 'center' });
        pdf.addImage(this.filePreviewUrls['secondaryInsuranceCardFront'], 'JPEG', 20, 30, 170, 100);
      }

      if (this.filePreviewUrls['secondaryInsuranceCardBack']) {
        pdf.addPage();
        pdf.setFontSize(12);
        pdf.text('Secondary Insurance Card - Back', pageWidth / 2, 20, { align: 'center' });
        pdf.addImage(this.filePreviewUrls['secondaryInsuranceCardBack'], 'JPEG', 20, 30, 170, 100);
      }
    }

    return pdf.output('blob');
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  private getDocCategory(fieldName: string): number {
    // Insurance card category
    if (fieldName.includes('InsuranceCard')) return 131;
    return 0;
  }

  private getDescription(fieldName: string): string {
    const descriptions: { [key: string]: string } = {
      'primaryInsuranceCardFront': 'Primary Insurance Card Front',
      'primaryInsuranceCardBack': 'Primary Insurance Card Back',
      'secondaryInsuranceCardFront': 'Secondary Insurance Card Front',
      'secondaryInsuranceCardBack': 'Secondary Insurance Card Back'
    };
    return descriptions[fieldName] || 'Insurance Document';
  }

  removeFile(fieldName: string): void {
    delete this.selectedFiles[fieldName];
    delete this.filePreviewUrls[fieldName];
    this.isFormEdited = true;

    // Clear the file input
    const input = document.querySelector(`input[name="${fieldName}"]`) as HTMLInputElement;
    if (input) {
      input.value = '';
    }

    this.snackBar.open(`${this.getDescription(fieldName)} removed`, 'Close', {
      duration: 2000,
    });
  }

  hasFile(fieldName: string): boolean {
    return !!this.selectedFiles[fieldName];
  }

  isUploading(fieldName: string): boolean {
    return false; // We don't upload individually anymore
  }

  toggleSecondaryInsurance(): void {
    this.showSecondaryInsurance = !this.showSecondaryInsurance;
    this.insuranceForm.patchValue({ hasSecondaryInsurance: this.showSecondaryInsurance });

    // Clear secondary fields if hiding
    if (!this.showSecondaryInsurance) {
      this.insuranceForm.patchValue({
        secondaryInsuranceProvider: '',
        secondarySubscriberId: '',
        secondaryGroupNumber: '',
        secondaryPolicyHolderName: '',
        secondaryPolicyHolderDOB: ''
      });
    }
    this.isFormEdited = true;
  }

  onCancel(): void {
    this.router.navigate(['/dashboard/main']);
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }

}